import React, { useState, useEffect, useRef } from "react";
import { useNotify } from "../../../context/NotificationContext";
import { useNavigate } from "react-router-dom";
import { Card, Form, Input, Button, Select, DatePicker, Typography, Spin, Divider, Space, Collapse, Upload, Progress, Modal, Table, Row, Col, InputNumber, Badge } from "antd";
import {
  SaveOutlined,
  UndoOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  UploadOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import tvShowService from "../../../services/tvShowService";
import masterService from "../../../services/masterService";
import videoService from "../../../services/videoService";
import dayjs from "dayjs";
import CustomUploader from "../../../components/uploader/CustomUploader";
import ImgCrop from "antd-img-crop";
import {
  getImageUrl,
  noWhitespaceValidator,
} from "../../../utils/commonFunctions";
import {
  validateEpisodePayload,
  validateSeriesPayload,
} from "../../../utils/seriesValidation";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

const AddTvShow = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [form] = Form.useForm();
  const [showName, setShowName] = useState("");

  const handleValuesChange = (changedValues) => {
    if (changedValues.tv_show_name !== undefined) {
      setShowName(changedValues.tv_show_name);
      console.log("showName", changedValues.tv_show_name);
    }
  };



  // State management
  const [loading, setLoading] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [episodeSpinner, setEpisodeSpinner] = useState(false);

  // Dropdown data
  const [genres, setGenres] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [talents, setTalents] = useState([]);

  // TV Show data
  const [tvShowId, setTvShowId] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [selectedEpisode, setSelectedEpisode] = useState(null);

  // Episode management
  const [episodeModalVisible, setEpisodeModalVisible] = useState(false);
  const [episodeList, setEpisodeList] = useState([]);
  const [episodeForm] = Form.useForm();

  // Episode file uploads
  const [episodeImages, setEpisodeImages] = useState([]);
  const [episodeVideos, setEpisodeVideos] = useState([]);

  // File uploads
  const [showImages, setShowImages] = useState([]);

  // Multilingual data



  // Seasons management
  const [seasons, setSeasons] = useState([
    {
      season_no: 1,
      season_title: "",
      season_description: "",
      season_casts: "",
      season_directors: "",
      season_original_release_date: null,
      season_release_date: null,
      season_is_active: "Y",
      talent_list: [],
    },
  ]);

  // Season panels start collapsed, so a season that fails validation is not on
  // screen — and its fields are not even mounted — when its error is raised.
  // Controlling the open panel here lets revealSeasonError below open the
  // offending season and scroll to it, instead of the form rejecting a field
  // the user has no way of seeing.
  const [activeSeasonKeys, setActiveSeasonKeys] = useState([]);
  const seasonsSectionRef = useRef(null);

  // Panels are keyed by position in the array, but episodes are addressed by
  // season number. The two only coincide while the seasons are exactly 1..n in
  // order, so every place that turns a panel into "which season is this" reads
  // the number off the season itself.
  const seasonNoAt = (index) => seasons[index]?.season_no ?? index + 1;

  const revealSeasonError = (seasonIndex) => {
    setActiveSeasonKeys([String(seasonIndex)]);
    // Opening the panel by writing activeKey does not fire the Collapse's
    // onChange, so the selection side of that handler has to be repeated here.
    // Without it selectedSeason and episodeList stay on whichever season was
    // open before, and the episode controls under the revealed panel would act
    // on a different season than the one on screen.
    handleSeasonSelect(seasonNoAt(seasonIndex));
    // After the panel has been told to open, so the scroll lands on its
    // expanded height rather than the collapsed header.
    requestAnimationFrame(() => {
      seasonsSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  // Load dropdown data
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        setLoading(true);

        const commonParams = {
          perPage: 1000,
          currentPage: 1,
          searchText: "",
          status: "Y",
        };
        const [genresRes, ratingsRes, languagesRes, talentsRes] =
          await Promise.all([
            masterService.getGenres(commonParams),
            videoService.getAllVideoRatings({
              perPage: 1000,
              currentPage: 1,
              searchText: "",
              isAll: true,
            }),
            masterService.getVideoLanguages(commonParams),
            masterService.getTalentList({
              status: "Y",
              isAll: true,
            }),
          ]);

        const pickList = (res) =>
          res?.data?.data?.data || res?.data?.data || res?.data || [];
        setGenres(pickList(genresRes));
        setRatings(pickList(ratingsRes));
        setLanguages(pickList(languagesRes));
        setTalents(pickList(talentsRes));
      } catch (error) {
        console.error("Error loading dropdown data:", error);
        notify.error({ message: "Failed to load form data" });
      } finally {
        setLoading(false);
      }
    };

    loadDropdownData();
  }, []);

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      setSpinning(true);


      const sanitizedSeasons = seasons.map((season) => ({
        ...season,
        season_title: season.season_title?.trim() || "",
        season_description: season.season_description?.trim() || "",
        season_casts: season.season_casts?.trim() || "",
        season_directors: season.season_directors?.trim() || "",
      }));

      // Format dates and prepare data
      const formattedValues = {
        ...values,
        genre_id: Array.isArray(values.genre_list)
          ? values.genre_list.join(",")
          : values.genre_list,
        seasons_list: sanitizedSeasons,
        images: showImages,
        // An empty InputNumber gives undefined, which JSON.stringify drops from
        // the request entirely — the API would then see no star_rating at all
        // and keep whatever it had. Sent as null so an emptied field clears the
        // rating on the save after the first one.
        star_rating: values.star_rating ?? null,
      };

      // Validated as the payload, not as form state, and against the same rules
      // the API applies to it on arrival — utils/seriesValidation.js and the
      // API's seriesValidation.ts are mirrors, so anything accepted here is
      // accepted there. What the API knows and this page does not (whether the
      // season exists, whether the name is taken) still comes back as an error.
      const validation = validateSeriesPayload(formattedValues);
      if (!validation.valid) {
        notify.error({ message: validation.message });
        if (validation.seasonIndex !== undefined) {
          revealSeasonError(validation.seasonIndex);
        }
        return;
      }

      console.log("Submitting TV show data:", formattedValues);

      // After the first save this page keeps the series open so episodes can be
      // added, and saving again has to update that series. Posting a second time
      // only worked while the name was untouched — the API matches an existing
      // series by name — so renaming and saving created a duplicate series and
      // left the episodes on the first one.
      const isUpdate = Boolean(tvShowId);
      const response = isUpdate
        ? await tvShowService.editTvShow(tvShowId, formattedValues)
        : await tvShowService.addTvShow(formattedValues);
      console.log("Response:", response);

      // Both endpoints answer with the same envelope, so the outcome is read the
      // same way for either. Reading .message off it unguarded used to throw a
      // TypeError on any answer without the inner object, which the catch then
      // reported as "Failed to add TV show" whatever had actually happened.
      const result = response.data?.data;
      if (result?.status) {
        if (!isUpdate) {
          setTvShowId(result.tv_show_id?.[0]);
        }
        notify.success({
          message:
            result.message ||
            (isUpdate ? "TV Show updated successfully" : "TV Show added successfully"),
        });
        // Don't navigate away, allow user to add episodes
      } else {
        notify.error({
          message: result?.message || "Failed to save TV show",
        });
      }
    } catch (error) {
      console.error("Error adding TV show:", error);
      // error is the response the interceptor rejected with, so the server's
      // reason — a duplicate season number, a missing name — survives instead of
      // being flattened into the generic message.
      notify.error({
        message:
          error?.data?.data?.message ||
          error?.data?.message ||
          "Failed to save TV show",
      });
    } finally {
      setSpinning(false);
    }
  };

  // Handle form reset
  const handleReset = () => {
    form.resetFields();
    setSeasons([
      {
        season_no: 1,
        season_title: "",
        season_description: "",
        season_casts: "",
        season_directors: "",
        season_original_release_date: null,
        season_release_date: null,
        season_is_active: "Y",
        talent_list: [],
      },
    ]);
    setShowImages([]);
    setTvShowId(null);
    setEpisodeList([]);
    setSelectedSeason(1);
    setActiveSeasonKeys([]);
  };

  // Handle back navigation
  const handleBack = () => {
    navigate("/series");
  };

  // Add new season
  const addSeason = () => {
    setSeasons((prev) => [
      ...prev,
      {
        // One past the highest number in use rather than one past the count, so
        // the new season cannot collide with an existing one. The API rejects a
        // repeated number outright: (season_no, tv_show_id) is unique.
        season_no:
          prev.reduce(
            (highest, season) => Math.max(highest, Number(season.season_no) || 0),
            0
          ) + 1,
        season_title: "",
        season_description: "",
        season_casts: "",
        season_directors: "",
        season_original_release_date: null,
        season_release_date: null,
        season_is_active: "Y",
        talent_list: [],
      },
    ]);
  };

  // Update season data
  const updateSeason = (index, field, value) => {
    setSeasons((prev) =>
      prev.map((season, i) =>
        i === index ? { ...season, [field]: value } : season
      )
    );
  };

  // Remove season

  // Trailer file management




  // Show image management
  const addShowImage = () => {
    setShowImages((prev) => [
      ...prev,
      {
        lang_id: null,
        card_image: null,
        banner_image: null,
        verticle_movie_card_image: null,
        movie_det_banner_image: null,
        movie_logo: null,
      },
    ]);
  };

  const removeShowImage = (index) => {
    setShowImages((prev) => prev.filter((_, i) => i !== index));
  };

  const updateShowImage = (index, field, value) => {
    setShowImages((prev) =>
      prev.map((image, i) =>
        i === index ? { ...image, [field]: value } : image
      )
    );
  };

  // Series multilingual data functions


  // Upload handler for Show Images (card/banner)
  const onShowImageUpload = async (file, keys) => {
    try {
      const formData = new FormData();
      formData.append("image_type", "images");
      formData.append("folder_name", "tv_show");
      formData.append("image", file);
      const response = await masterService.uploadImage(formData);
      const s3Key = response.data?.data?.s3_key;
      // Without this guard an upload that returns no key still reported success
      // while leaving the field empty, so submit was then blocked by
      // "Please complete all added series image sets" for a row the user had
      // just watched succeed. Same check onSeoImageUpload already makes.
      if (!s3Key) {
        notify.error({
          message: "Image upload did not return a file, please try again",
        });
        return;
      }
      // updateShowImage replaces the row instead of writing into it — the
      // previous [...prev] was a shallow copy, so assigning through it mutated
      // the object still held in state.
      updateShowImage(keys.index, keys.path, s3Key);
      notify.success({ message: "Image uploaded successfully" });
    } catch (error) {
      notify.error({ message: "Failed to upload image" });
    }
  };









  // Season multilingual data functions



  // Episode management
  const handleAddEpisode = () => {
    if (!tvShowId) {
      notify.error({ message: "Save Series First" });
      return;
    }
    setSelectedEpisode(null);
    episodeForm.resetFields();
    episodeForm.setFieldsValue({
      season_no: selectedSeason,
      episode_is_active: "Y",
    });
    // Reset episode file arrays
    setEpisodeImages([]);
    setEpisodeVideos([]);
    setEpisodeModalVisible(true);
  };

  const handleEditEpisode = (record) => {
    setSelectedEpisode(record);
    episodeForm.setFieldsValue({
      ...record,
      release_date: record.release_date ? dayjs(record.release_date) : null,
    });
    // Load existing episode files if available
    setEpisodeImages(record.images || []);
    setEpisodeVideos(record.video_files || []);
    setEpisodeModalVisible(true);
  };

  const handleEpisodeSubmit = async (values) => {
    try {
      setEpisodeSpinner(true);


      const episodeData = {
        ...values,
        tv_show_id: tvShowId,
        release_date: values.release_date
          ? values.release_date.format("YYYY-MM-DD HH:mm:ss")
          : null,
        images: episodeImages,
        video_files: episodeVideos,
      };

      // The same rules the API applies to this payload on arrival. What it
      // knows and this page does not — whether the season exists, whether the
      // episode number is taken — still comes back as an error message.
      const validation = validateEpisodePayload(episodeData);
      if (!validation.valid) {
        notify.error({ message: validation.message });
        return;
      }

      let response;
      if (selectedEpisode) {
        response = await tvShowService.editTvShowEpisode(
          selectedEpisode.episode_id,
          episodeData
        );
      } else {
        response = await tvShowService.addTvShowEpisode(episodeData);
      }

      if (response.data && response.data.status) {
        notify.success({
          message: response.data.message || "Episode saved successfully",
        });
        setEpisodeModalVisible(false);
        episodeForm.resetFields();
        // Reset episode file arrays
        setEpisodeImages([]);
        setEpisodeVideos([]);
        // Refresh the season the episode was saved under. Called with nothing it
        // asked the API for season "undefined", which came back as an error, so
        // the table still showed the list from before the save.
        loadEpisodes(episodeData.season_no ?? selectedSeason);
      } else {
        notify.error({
          message: response.data?.message || "Failed to save episode",
        });
      }
    } catch (error) {
      console.error("Error saving episode:", error);
      // The api interceptor rejects with the response, so the reason the server
      // gave — a duplicate episode number, a season that does not exist — is on
      // error.data. It used to be replaced by the generic message below.
      notify.error({
        message: error?.data?.message || "Failed to save episode",
      });
    } finally {
      setEpisodeSpinner(false);
    }
  };

  // Defaults to the season on screen so a caller that omits it still asks for a
  // real season rather than for season "undefined".
  const loadEpisodes = async (seasonNo = selectedSeason) => {
    if (!tvShowId) return;

    try {
      setLoading(true);
      const response = await tvShowService.getAllTvShowEpisode({
        tv_show_id: tvShowId,
        season_no: seasonNo,
      });

      if (response.data && response.data.status) {
        setEpisodeList(response.data.data.data || []);
      }
    } catch (error) {
      console.error("Error loading episodes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeasonSelect = (seasonNo) => {
    console.log("seasonNo", seasonNo);
    setSelectedSeason(seasonNo);
    loadEpisodes(seasonNo);
  };

  // Episode file management
  const addEpisodeImage = () => {
    setEpisodeImages((prev) => [
      ...prev,
      {
        lang_id: null,
        card_image: null,
        banner_image: null,
        vertical_card_image: null,
        verticle_movie_card_image: null,
        movie_det_banner_image: null,
        movie_logo: null,
      },
    ]);
  };

  const removeEpisodeImage = (index) => {
    setEpisodeImages((prev) => prev.filter((_, i) => i !== index));
  };

  const updateEpisodeImage = (index, field, value) => {
    setEpisodeImages((prev) =>
      prev.map((image, i) =>
        i === index ? { ...image, [field]: value } : image
      )
    );
  };

  const addEpisodeVideo = () => {
    setEpisodeVideos((prev) => [
      ...prev,
      {
        lang: null,
        file: null,
        file_name: null,
        percent: 0,
        bunny_guid: null,
        video_id: null,
      },
    ]);
  };

  const removeEpisodeVideo = (index) => {
    setEpisodeVideos((prev) => prev.filter((_, i) => i !== index));
  };

  const updateEpisodeVideo = (index, field, value) => {
    setEpisodeVideos((prev) =>
      prev.map((video, i) =>
        i === index ? { ...video, [field]: value } : video
      )
    );
  };

  const onEpisodeVideoUploadComplete = (index, uploadData) => {
    const updated = [...episodeVideos];
    updated[index].file_name = uploadData.file_name;
    updated[index].percent = uploadData.progress;
    updated[index].bunny_guid = uploadData.bunny_guid;
    updated[index].video_id = uploadData.video_id;
    setEpisodeVideos(updated);
  };







  // Episode image upload
  const handleEpisodeImageUpload = async (file, keys) => {
    try {
      const formData = new FormData();
      formData.append("image_type", "images");
      formData.append("folder_name", "tv_show");
      formData.append("image", file);
      const response = await masterService.uploadImage(formData);
      // Optional chaining as well as the guard: response.data.data.s3_key threw
      // a TypeError when the payload lacked `data`, which the catch reported as
      // the misleading "Failed to upload image".
      const s3Key = response.data?.data?.s3_key;
      if (!s3Key) {
        notify.error({
          message: "Image upload did not return a file, please try again",
        });
        return;
      }
      updateEpisodeImage(keys.index, keys.path, s3Key);
      notify.success({ message: "Image uploaded successfully" });
    } catch (e) {
      notify.error({ message: "Failed to upload image" });
    }
  };

  // Episode table columns
  const episodeColumns = [
    {
      title: "Episode No",
      dataIndex: "episode_no",
      key: "episode_no",
      sorter: true,
    },
    {
      title: "Title",
      dataIndex: "episode_title",
      key: "episode_title",
      sorter: true,
    },
    {
      title: "Run Time",
      dataIndex: "episode_runtime",
      key: "episode_runtime",
      sorter: true,
    },
    {
      title: "Release Date",
      dataIndex: "release_date",
      key: "release_date",
      sorter: true,
    },
    {
      title: "Status",
      dataIndex: "episode_is_active",
      key: "episode_is_active",
      render: (text) => (text === "Y" ? "Active" : "Inactive"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditEpisode(record)}
        >
          Edit
        </Button>
      ),
    },
  ];

  // Header content with user info

  return (
    <>
      <Spin spinning={spinning}>
        <Card>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={handleValuesChange}
            initialValues={{
              is_subscription_required: "N",
              tv_show_is_active: "Y",
            }}
          >
            {/* Basic Information Section */}
            <div style={{ marginBottom: "32px" }}>
              <Title
                level={3}
                style={{
                  marginBottom: "24px",
                  color: "#1890ff",
                  borderBottom: "2px solid #f0f0f0",
                  paddingBottom: "8px",
                  textAlign: "left",
                }}
              >
                Basic Information
              </Title>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "24px",
                  marginBottom: "24px",
                }}
              >
                <Form.Item
                  name="tv_show_name"
                  label="Series Name"
                  rules={[
                    { required: true, message: "Please enter series name" },
                    { validator: noWhitespaceValidator },
                  ]}
                >
                  <Input placeholder="Enter series name" size="large" />
                </Form.Item>


                <Form.Item name="rating_description" label="Rating Description">
                  <Input placeholder="Enter rating description" size="large" />
                </Form.Item>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "24px",
                  marginBottom: "24px",
                }}
              >

                <Form.Item name="talent_list" label="Talent List">
                  <Select
                    placeholder="Select talent"
                    size="large"
                    mode="multiple"
                    showSearch
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {talents.map((talent) => (
                      <Option key={talent.talent_id} value={talent.talent_id}>
                        {talent.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

            </div>

            <Divider style={{ margin: "32px 0" }} />

            {/* Classification & Settings Section */}
            <div style={{ marginBottom: "32px" }}>
              <Title
                level={3}
                style={{
                  marginBottom: "24px",
                  color: "#1890ff",
                  borderBottom: "2px solid #f0f0f0",
                  paddingBottom: "8px",
                  textAlign: "left",
                }}
              >
                Classification & Settings
              </Title>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "24px",
                  marginBottom: "24px",
                }}
              >
                <Form.Item
                  name="lang_list"
                  label="Show Languages"
                  rules={[
                    { required: true, message: "Please select languages" },
                  ]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select languages"
                    loading={loading}
                    showSearch
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    size="large"
                  >
                    {languages.map((lang) => (
                      <Option key={lang.lang_id} value={lang.lang_id}>
                        {lang.lang_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="genre_list"
                  label="Show Genre"
                  rules={[{ required: true, message: "Please select genre" }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select genres"
                    loading={loading}
                    showSearch
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    size="large"
                  >
                    {genres.map((genre) => (
                      <Option key={genre.genre_id} value={genre.genre_id}>
                        {genre.genre_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: "24px",
                  marginBottom: "24px",
                }}
              >
                <Form.Item
                  name="rating_id"
                  label="Content Rating"
                  rules={[{ required: true, message: "Please select rating" }]}
                >
                  <Select
                    placeholder="Select rating"
                    loading={loading}
                    showSearch
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    size="large"
                  >
                    {ratings.map((rating) => (
                      <Option key={rating.vr_id} value={rating.vr_id}>
                        {rating.rating_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="content_type" label="Content Type">
                  <Select
                    placeholder="Select content type"
                    size="large"
                    allowClear
                  >
                    <Option value="Originals">Originals</Option>
                    <Option value="Exclusive">Exclusive</Option>
                  </Select>
                </Form.Item>
                {/* The star rating shown next to the series, stored as
                    v_tv_show.star_rating and returned by the API as `rating`.
                    Nothing to do with Content Rating beside it, which is the age
                    certification. Optional: left empty the API answers null
                    rather than inventing a number. */}
                <Form.Item name="star_rating" label="Rating">
                  <InputNumber
                    min={0}
                    max={10}
                    step={0.1}
                    placeholder="e.g. 4.6"
                    size="large"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: "24px",
                  marginBottom: "24px",
                }}
              >
                <Form.Item
                  name="is_subscription_required"
                  label="Subscription Required"
                  rules={[
                    {
                      required: true,
                      message: "Please select subscription requirement",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select subscription requirement"
                    size="large"
                  >
                    <Option value="Y">Yes</Option>
                    <Option value="N">No</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="tv_show_is_active"
                  label="Status"
                  rules={[{ required: true, message: "Please select status" }]}
                >
                  <Select placeholder="Select status" size="large">
                    <Option value="Y">Active</Option>
                    <Option value="N">Inactive</Option>
                  </Select>
                </Form.Item>

              </div>
            </div>

            <Divider style={{ margin: "32px 0" }} />

            {/* Media Files Section */}
            <div style={{ marginBottom: "32px" }}>
              <Title
                level={3}
                style={{
                  marginBottom: "24px",
                  color: "#1890ff",
                  borderBottom: "2px solid #f0f0f0",
                  paddingBottom: "8px",
                  textAlign: "left",
                }}
              >
                Media Files
              </Title>

              {/* Show Images Subsection */}
              <div style={{ marginBottom: "32px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <Title
                    level={4}
                    style={{ margin: 0, color: "#fa8c16", textAlign: "left" }}
                  >
                    Show Images
                  </Title>
                  <Button
                    type="dashed"
                    onClick={addShowImage}
                    icon={<UploadOutlined />}
                  >
                    Add Image Set
                  </Button>
                </div>
                {showImages.length === 0 && (
                  <Card
                    style={{
                      textAlign: "center",
                      padding: "24px",
                      background: "#fafafa",
                    }}
                  >
                    <Text type="secondary">
                      No image sets added yet. Click "Add Image Set" to get
                      started.
                    </Text>
                  </Card>
                )}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  {showImages.map((image, index) => (
                    <Card key={index} style={{ marginBottom: "16px" }}>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item label="Choose Image language" required>
                            <Select
                              value={image.lang_id}
                              onChange={(value) =>
                                updateShowImage(index, "lang_id", value)
                              }
                              placeholder="Please select image language"
                              size="large"
                              // Locked once images exist so they cannot be
                              // reassigned to another language after upload —
                              // but only if a language was actually chosen.
                              // Uploading before picking one used to disable
                              // this while lang_id was still null, leaving the
                              // set permanently failing "language required"
                              // with no way to set it but to delete the set.
                              disabled={Boolean(
                                image.lang_id &&
                                  (image.card_image ||
                                    image.banner_image ||
                                    image.verticle_movie_card_image ||
                                    image.movie_det_banner_image ||
                                    image.movie_logo)
                              )}
                              showSearch
                              filterOption={(input, option) =>
                                option.children
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                              }
                            >
                              {languages.map((lang) => (
                                <Option key={lang.lang_id} value={lang.lang_id}>
                                  {lang.lang_name}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => removeShowImage(index)}
                          >
                            Remove Image
                          </Button>
                        </Col>
                      </Row>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item label="Upload card image">
                            {image.card_image && (
                              <div style={{ marginBottom: "12px" }}>
                                <div
                                  style={{
                                    padding: "8px 12px",
                                    background: "#fff7e6",
                                    border: "1px solid #ffd591",
                                    borderRadius: 6,
                                    color: "#fa8c16",
                                    marginBottom: "8px",
                                  }}
                                >
                                  <Text strong>
                                    {getImageUrl(image.card_image)}
                                  </Text>
                                </div>
                                <div
                                  style={{
                                    width: "100%",
                                    height: "120px",
                                    border: "1px solid #d9d9d9",
                                    borderRadius: 6,
                                    overflow: "hidden",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: "#fafafa",
                                  }}
                                >
                                  <img
                                    src={getImageUrl(image.card_image)}
                                    alt="Card preview"
                                    style={{
                                      maxWidth: "100%",
                                      maxHeight: "100%",
                                      objectFit: "contain",
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                            <ImgCrop
                              aspect={300 / 400}
                              rotationSlider
                              quality={1}
                            >
                              <Upload
                                accept="image/*"
                                showUploadList={false}
                                beforeUpload={(file) => {
                                  onShowImageUpload(file, {
                                    index,
                                    path: "card_image",
                                  });
                                  return false;
                                }}
                              >
                                <Button
                                  icon={<UploadOutlined />}
                                  size="large"
                                  style={{ width: "100%" }}
                                >
                                  {image.card_image
                                    ? "Change Card Image"
                                    : "Upload Card Image"}
                                </Button>
                              </Upload>
                            </ImgCrop>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item label="Upload banner image">
                            {image.banner_image && (
                              <div style={{ marginBottom: "12px" }}>
                                <div
                                  style={{
                                    padding: "8px 12px",
                                    background: "#fff7e6",
                                    border: "1px solid #ffd591",
                                    borderRadius: 6,
                                    color: "#fa8c16",
                                    marginBottom: "8px",
                                  }}
                                >
                                  <Text strong>
                                    {getImageUrl(image.banner_image)}
                                  </Text>
                                </div>
                                <div
                                  style={{
                                    width: "100%",
                                    height: "120px",
                                    border: "1px solid #d9d9d9",
                                    borderRadius: 6,
                                    overflow: "hidden",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: "#fafafa",
                                  }}
                                >
                                  <img
                                    src={getImageUrl(image.banner_image)}
                                    alt="Banner preview"
                                    style={{
                                      maxWidth: "100%",
                                      maxHeight: "100%",
                                      objectFit: "contain",
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                            <ImgCrop
                              aspect={1280 / 720}
                              rotationSlider
                              quality={1}
                            >
                              <Upload
                                accept="image/*"
                                showUploadList={false}
                                beforeUpload={(file) => {
                                  onShowImageUpload(file, {
                                    index,
                                    path: "banner_image",
                                  });
                                  return false;
                                }}
                              >
                                <Button
                                  icon={<UploadOutlined />}
                                  size="large"
                                  style={{ width: "100%" }}
                                >
                                  {image.banner_image
                                    ? "Change Banner Image"
                                    : "Upload Banner Image"}
                                </Button>
                              </Upload>
                            </ImgCrop>
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </div>
              </div>
            </div>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={spinning}
                  size="large"
                >
                  Save Series
                </Button>
                <Button
                  onClick={handleReset}
                  icon={<UndoOutlined />}
                  size="large"
                >
                  Reset
                </Button>
                <Button
                  onClick={handleBack}
                  icon={<ArrowLeftOutlined />}
                  size="large"
                >
                  Go Back
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        {/* Seasons Section */}
        <Card style={{ marginTop: "24px" }}>
          <div ref={seasonsSectionRef} style={{ marginBottom: "32px" }}>
            <Title
              level={3}
              style={{
                marginBottom: "24px",
                color: "#1890ff",
                borderBottom: "2px solid #f0f0f0",
                paddingBottom: "8px",
                textAlign: "left",
              }}
            >
              Seasons
            </Title>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <Title level={4} style={{ margin: 0 }}>
                Seasons Management
              </Title>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={addSeason}
                size="large"
              >
                Add Season
              </Button>
            </div>
          </div>

          <Collapse
            accordion={true}
            activeKey={activeSeasonKeys}
            onChange={(key) => {
              const keys = key == null ? [] : [].concat(key);
              setActiveSeasonKeys(keys);
              if (keys.length > 0) {
                const seasonIndex = parseInt(keys[0]);
                handleSeasonSelect(seasonNoAt(seasonIndex));
              }
            }}
          >
            {seasons.map((season, index) => (
              <Panel
                header={`Season ${season.season_no}`}
                key={index}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "24px",
                  }}
                >
                  <div>
                    <label>Season Title</label>
                    <Input
                      value={season.season_title}
                      onChange={(e) => {
                        const value = e.target.value;
                        updateSeason(index, "season_title", value);
                      }}
                      onBlur={(e) => {
                        const newData = [...seasons];
                        newData[index].season_title = e.target.value.trim();
                        setSeasons(newData);
                      }}
                      placeholder="Enter season title"
                      size="large"
                    />
                  </div>

                  <div>
                    <label>Season Status</label>
                    <Select
                      value={season.season_is_active}
                      onChange={(value) =>
                        updateSeason(index, "season_is_active", value)
                      }
                      style={{ width: "100%" }}
                      size="large"
                    >
                      <Option value="Y">Active</Option>
                      <Option value="N">Inactive</Option>
                    </Select>
                  </div>
                </div>

                <div style={{ marginTop: "24px" }}>
                  <label>Season Description</label>
                  <TextArea
                    value={season.season_description}
                    onChange={(e) =>
                      updateSeason(index, "season_description", e.target.value)
                    }
                    onBlur={(e) => {
                      const newData = [...seasons];
                      newData[index].season_description = e.target.value.trim();
                      setSeasons(newData);
                    }}
                    placeholder="Enter season description"
                    rows={3}
                    maxLength={2000}
                    showCount
                    size="large"
                  />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "24px",
                    marginTop: "24px",
                  }}
                >
                  {/* Deliberately not a Form.Item: this select is rendered once
                      per season, and the form already has a talent_list field for
                      the series itself. Sharing that name made all of them one
                      value, so picking a season's talent also rewrote the
                      series' — while the season actually submitted whatever
                      updateSeason had stored. Bound to the season, like every
                      other field in this panel. */}
                  <div>
                    <label>Talent List</label>
                    <Select
                      placeholder="Select talent"
                      size="large"
                      mode="multiple"
                      showSearch
                      style={{ width: "100%" }}
                      value={season.talent_list}
                      onChange={(value) =>
                        updateSeason(index, "talent_list", value)
                      }
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {talents.map((talent) => (
                        <Option key={talent.talent_id} value={talent.talent_id}>
                          {talent.name}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </div>

                {/* Country of origin and the intro/outro markers used to be
                    repeated here, inside the per-season panel, although they
                    describe the series: every season rendered another Form.Item
                    under the same name, so all of them shared one value and the
                    last panel mounted decided what was submitted. They are
                    declared once now, in Basic Information above. */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "24px",
                    marginTop: "24px",
                  }}
                >
                  <div>
                    <label>Original Release Date</label>
                    <DatePicker
                      value={
                        season.season_original_release_date
                          ? season.season_original_release_date
                          : null
                      }
                      onChange={(date) =>
                        updateSeason(
                          index,
                          "season_original_release_date",
                          date
                        )
                      }
                      showTime
                      format="YYYY-MM-DD HH:mm:ss"
                      placeholder="Select original release date"
                      style={{ width: "100%" }}
                      size="large"
                    />
                  </div>

                  <div>
                    <label>Release Date</label>
                    <DatePicker
                      value={
                        season.season_release_date
                          ? season.season_release_date
                          : null
                      }
                      onChange={(date) =>
                        updateSeason(index, "season_release_date", date)
                      }
                      showTime
                      format="YYYY-MM-DD HH:mm:ss"
                      placeholder="Select release date"
                      style={{ width: "100%" }}
                      size="large"
                    />
                  </div>
                </div>

                <Divider />

                <Divider style={{ margin: "24px 0" }} />

                {/* Episode Management */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <Title level={4} style={{ margin: 0, color: "#722ed1" }}>
                    Episodes
                  </Title>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddEpisode}
                    size="large"
                  >
                    Add Episode
                  </Button>
                </div>

                <Table
                  dataSource={episodeList}
                  columns={episodeColumns}
                  rowKey="episode_id"
                  loading={loading}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                  }}
                />
              </Panel>
            ))}
          </Collapse>
        </Card>
      </Spin>

      {/* Episode Modal */}
      <Modal
        title={selectedEpisode ? "Edit Episode" : "Add Episode"}
        open={episodeModalVisible}
        onCancel={() => setEpisodeModalVisible(false)}
        footer={null}
        width={800}
      >
        <Spin spinning={episodeSpinner}>
          <Form
            form={episodeForm}
            layout="vertical"
            onFinish={handleEpisodeSubmit}
            initialValues={{
                      episode_is_active: "Y",
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="season_no"
                  label="Season No"
                  rules={[
                    { required: true, message: "Please enter season number" },
                    {
                      pattern: /^(0|[1-9]\d*)$/,
                      message: "Season number must be a non-negative integer",
                    },
                  ]}
                  disabled
                >
                  <InputNumber
                    type="number"
                    placeholder="Enter season number"
                    size="large"
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="episode_no"
                  label="Episode No"
                  rules={[
                    { required: true, message: "Please enter episode number" },
                    {
                      pattern: /^(0|[1-9]\d*)$/,
                      message: "Episode number must be a non-negative integer",
                    },
                  ]}
                >
                  <InputNumber
                    type="number"
                    placeholder="Enter episode number"
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="episode_title"
              label="Episode Title"
              rules={[
                { required: true, message: "Please enter episode title" },
                { validator: noWhitespaceValidator },
              ]}
            >
              <Input placeholder="Enter episode title" size="large" />
            </Form.Item>

            <Form.Item
              name="episode_summary"
              label="Episode Summary"
              rules={[
                { required: true, message: "Please enter episode summary" },
                { validator: noWhitespaceValidator },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="Enter episode summary"
                maxLength={2000}
                showCount
                size="large"
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="episode_runtime"
                  label="Running Time (in minutes)"
                  rules={[
                    { required: true, message: "Please enter running time" },
                    {
                      pattern: /^(0|[1-9]\d*)$/,
                      message: "Running time must be a non-negative integer",
                    },
                  ]}
                >
                  <InputNumber
                    type="number"
                    placeholder="Enter running time"
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="episode_endtime"
                  label="End Time (in minutes)"
                  rules={[
                    { required: true, message: "Please enter end time" },
                    {
                      pattern: /^(0|[1-9]\d*)$/,
                      message: "End time must be a non-negative integer",
                    },
                  ]}
                >
                  <InputNumber
                    type="number"
                    placeholder="Enter end time"
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="release_date"
              label="Release Date"
              rules={[
                { required: true, message: "Please select release date" },
              ]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm:ss"
                placeholder="Select release date"
                style={{ width: "100%" }}
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="episode_is_active"
              label="Status"
              rules={[{ required: true, message: "Please select status" }]}
            >
              <Select placeholder="Select status" size="large">
                <Option value="Y">Active</Option>
                <Option value="N">Inactive</Option>
              </Select>
            </Form.Item>

            <Divider style={{ margin: "24px 0" }} />

            {/* Episode Images Section */}
            <div style={{ marginBottom: "24px" }}>
              <Title
                level={4}
                style={{ marginBottom: "16px", color: "#fa8c16" }}
              >
                Episode Images
              </Title>
              <div style={{ marginBottom: "16px" }}>
                <Button
                  type="dashed"
                  onClick={addEpisodeImage}
                  icon={<UploadOutlined />}
                >
                  Add Image Set
                </Button>
              </div>

              {episodeImages.length === 0 && (
                <Card
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    background: "#fafafa",
                    marginBottom: "12px",
                  }}
                >
                  <Text type="secondary">No image sets added yet.</Text>
                </Card>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "16px",
                }}
              >
                {episodeImages.map((image, index) => (
                  <Card key={`episode-img-${index}`}>
                    <Form.Item
                      label="Image Language"
                      style={{ marginBottom: "12px" }}
                    >
                      <Select
                        value={image.lang_id}
                        onChange={(value) =>
                          updateEpisodeImage(index, "lang_id", value)
                        }
                        placeholder="Select image language"
                        size="large"
                        showSearch
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {languages.map((lang) => (
                          <Option key={lang.lang_id} value={lang.lang_id}>
                            {lang.lang_name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "16px",
                      }}
                    >
                      <Form.Item
                        label="Card Image (300x400)"
                        style={{ marginBottom: 0 }}
                      >
                        {image.card_image && (
                          <div style={{ marginBottom: "12px" }}>
                            <div
                              style={{
                                padding: "8px 12px",
                                background: "#fff7e6",
                                border: "1px solid #ffd591",
                                borderRadius: 6,
                                color: "#fa8c16",
                                marginBottom: "8px",
                              }}
                            >
                              <Text strong>
                                {getImageUrl(image.card_image)}
                              </Text>
                            </div>
                            <div
                              style={{
                                width: "100%",
                                height: "120px",
                                border: "1px solid #d9d9d9",
                                borderRadius: 6,
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#fafafa",
                              }}
                            >
                              <img
                                src={getImageUrl(image.card_image)}
                                alt="Card preview"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  objectFit: "contain",
                                }}
                              />
                            </div>
                          </div>
                        )}
                        <ImgCrop aspect={300 / 400} rotationSlider quality={1}>
                          <Upload
                            accept="image/*"
                            showUploadList={false}
                            beforeUpload={(f) => {
                              handleEpisodeImageUpload(f, {
                                index,
                                path: "card_image",
                              });
                              return false;
                            }}
                          >
                            <Button
                              icon={<UploadOutlined />}
                              style={{ width: "100%" }}
                              size="large"
                            >
                              {image.card_image
                                ? "Change Card Image"
                                : "Upload Card Image"}
                            </Button>
                          </Upload>
                        </ImgCrop>
                      </Form.Item>

                      <Form.Item
                        label="Banner Image (1280x720)"
                        style={{ marginBottom: 0 }}
                      >
                        {image.banner_image && (
                          <div style={{ marginBottom: "12px" }}>
                            <div
                              style={{
                                padding: "8px 12px",
                                background: "#fff7e6",
                                border: "1px solid #ffd591",
                                borderRadius: 6,
                                color: "#fa8c16",
                                marginBottom: "8px",
                              }}
                            >
                              <Text strong>
                                {getImageUrl(image.banner_image)}
                              </Text>
                            </div>
                            <div
                              style={{
                                width: "100%",
                                height: "120px",
                                border: "1px solid #d9d9d9",
                                borderRadius: 6,
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#fafafa",
                              }}
                            >
                              <img
                                src={getImageUrl(image.banner_image)}
                                alt="Banner preview"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  objectFit: "contain",
                                }}
                              />
                            </div>
                          </div>
                        )}
                        <ImgCrop aspect={1280 / 720} rotationSlider quality={1}>
                          <Upload
                            accept="image/*"
                            showUploadList={false}
                            beforeUpload={(f) => {
                              handleEpisodeImageUpload(f, {
                                index,
                                path: "banner_image",
                              });
                              return false;
                            }}
                          >
                            <Button
                              icon={<UploadOutlined />}
                              style={{ width: "100%" }}
                              size="large"
                            >
                              {image.banner_image
                                ? "Change Banner Image"
                                : "Upload Banner Image"}
                            </Button>
                          </Upload>
                        </ImgCrop>
                      </Form.Item>

                      {/* The episode's tall poster. Same upload path and same
                          per-language set as the card image above it; only the
                          crop differs — 360x600, the size vertical_card_image
                          is filled at everywhere else in the dashboard. */}
                      <Form.Item
                        label="Vertical Card Image (360x600)"
                        style={{ marginBottom: 0 }}
                      >
                        {image.vertical_card_image && (
                          <div style={{ marginBottom: "12px" }}>
                            <div
                              style={{
                                padding: "8px 12px",
                                background: "#fff7e6",
                                border: "1px solid #ffd591",
                                borderRadius: 6,
                                color: "#fa8c16",
                                marginBottom: "8px",
                              }}
                            >
                              <Text strong>
                                {getImageUrl(image.vertical_card_image)}
                              </Text>
                            </div>
                            <div
                              style={{
                                width: "100%",
                                height: "120px",
                                border: "1px solid #d9d9d9",
                                borderRadius: 6,
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#fafafa",
                              }}
                            >
                              <img
                                src={getImageUrl(image.vertical_card_image)}
                                alt="Vertical card preview"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  objectFit: "contain",
                                }}
                              />
                            </div>
                          </div>
                        )}
                        <ImgCrop aspect={360 / 600} rotationSlider quality={1}>
                          <Upload
                            accept="image/*"
                            showUploadList={false}
                            beforeUpload={(f) => {
                              handleEpisodeImageUpload(f, {
                                index,
                                path: "vertical_card_image",
                              });
                              return false;
                            }}
                          >
                            <Button
                              icon={<UploadOutlined />}
                              style={{ width: "100%" }}
                              size="large"
                            >
                              {image.vertical_card_image
                                ? "Change Vertical Card Image"
                                : "Upload Vertical Card Image"}
                            </Button>
                          </Upload>
                        </ImgCrop>
                      </Form.Item>
                    </div>

                    <div style={{ marginTop: 12, textAlign: "right" }}>
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeEpisodeImage(index)}
                      >
                        Remove Image Set
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <Divider style={{ margin: "24px 0" }} />

            {/* Episode Video Files Section */}
            <div style={{ marginBottom: "24px" }}>
              <Title
                level={4}
                style={{ marginBottom: "16px", color: "#1890ff" }}
              >
                Episode Video Files
              </Title>
              <div style={{ marginBottom: "16px" }}>
                <Button
                  type="dashed"
                  onClick={addEpisodeVideo}
                  icon={<UploadOutlined />}
                >
                  Add Video
                </Button>
              </div>

              {episodeVideos.length === 0 && (
                <Card
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    background: "#fafafa",
                    marginBottom: "12px",
                  }}
                >
                  <Text type="secondary">No video files added yet.</Text>
                </Card>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                }}
              >
                {episodeVideos.map((video, index) => (
                  <div key={`episode-video-${index}`}>
                    <Form.Item
                      label="Video Language"
                      style={{ marginBottom: "16px" }}
                    >
                      <Select
                        value={video.lang}
                        onChange={(value) =>
                          updateEpisodeVideo(index, "lang", value)
                        }
                        placeholder="Select video language"
                        size="large"
                        disabled={video.file_name && video.bunny_guid}
                        showSearch
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {languages.map((lang) => (
                          <Option key={lang.lang_id} value={lang.lang_id}>
                            {lang.lang_name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <CustomUploader
                      fileType="episode"
                      language={
                        video.lang
                          ? languages.find((l) => l.lang_id === video.lang)
                              ?.lang_name
                          : ""
                      }
                      movieName={showName}
                      onUploadComplete={(uploadData) =>
                        onEpisodeVideoUploadComplete(index, uploadData)
                      }
                      onRemove={() => removeEpisodeVideo(index)}
                      type="episode"
                      existingFileName={video.file_name}
                      seasonNo={episodeForm.getFieldValue("season_no")}
                      episodeNo={episodeForm.getFieldValue("episode_no")}
                    />
                  </div>
                ))}
              </div>
            </div>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={episodeSpinner}
                >
                  {selectedEpisode ? "Update Episode" : "Add Episode"}
                </Button>
                <Button
                  onClick={() => {
                    episodeForm.resetFields();
                    setEpisodeImages([]);
                    setEpisodeVideos([]);
                    setEpisodeModalVisible(false);
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </>
  );
};

export default AddTvShow;
